/**
 * 出错时给玩家看的那一块怎么说（ERROR-RECOVERY-01；总账 S-086、R13-26、N-09）：按错误码、HTTP 状态归成几类，
 * 每一类一个标题、一句人话、像不像出错、原地再试一次有没有用。StateView 的 ErrorState 用它；页面要自己摆出错的样子时也用它，不各写一套。
 * - 还没开放（CAPABILITY_UNAVAILABLE / NOT_CONFIGURED）、后端还没有这条路（路由级 404/405）：平静地说明（quiet），不像出错；
 * - 401 去登录；403 看不到了；404 没有找到；409 情况刚有变化；422 说哪一项没填好；429 歇一会儿；
 *   断网、超时、502/503/504 信号暂时中断（可以再试一次）；后端明说重试也没用的 503 现在还不行；
 * - 认得的原因码（pet_required、household_required）换成人话，原因码本身不给玩家看；
 * - 手机号、邮箱注册那六个错误码（I108）：不论回的是什么状态，都是“没能完成这一步”＋一句人话（errors.ts 的 playerTextForCode），不给重试。
 * 用户 10-09 定稿（X-09／U-26）：玩家端任何地方都不显示错误码、请求编号，也不做“展开看详情”的折叠区；要查只在运营后台查。
 * 这里只出给人看的字，ApiError 里的 code、requestId 留给日志和运营后台，不往页面上放。
 */
import { playerTextForCode, toApiError, type ApiError } from "./errors";

export type PlayerErrorKind =
  | "unavailable"
  | "noRoad"
  | "login"
  | "forbidden"
  | "notFound"
  | "choose"
  | "conflict"
  | "fixInput"
  | "slowDown"
  | "signal"
  | "notNow"
  | "failed";

export interface PlayerErrorView {
  kind: PlayerErrorKind;
  title: string;
  text: string;
  /** error：出错（红色、role=alert）；quiet：还没开放、还没有路，平静地说明，不像出错（S-086） */
  tone: "error" | "quiet";
  /** 原地再试一次有没有用：有用时给“重试”；没用时页面给回上一步、去别处（就近恢复） */
  retry: boolean;
}

/**
 * 有的拒绝原因，后端原话里写着参数名（pet_required：“……请指明是哪一只（pet_id）。”）。按原因码换成人话；
 * 后端把原话改好以后这里照样按原因码，不妨碍。
 */
const REASON_VIEW: Record<string, { title: string; text: string }> = {
  pet_required: { title: "先选一只伙伴", text: "家里有不止一只伙伴，先选一只再看。" },
  household_required: { title: "先选一个家", text: "你在不止一个家里，先选好是哪一个家再看。" },
};

const CONFLICT_CODES = new Set<ApiError["code"]>(["CONFLICT", "VERSION_CONFLICT", "IDEMPOTENCY_KEY_REUSED", "IDEMPOTENCY_IN_PROGRESS"]);

export function describeError(error: unknown): PlayerErrorView {
  const err = toApiError(error);
  const reason = typeof err.details?.reason === "string" ? err.details.reason : null;
  const known = reason && Object.prototype.hasOwnProperty.call(REASON_VIEW, reason) ? REASON_VIEW[reason] : null;
  const text = known ? known.text : err.playerMessage;
  if (err.isCapabilityUnavailable) return { kind: "unavailable", title: "这里暂时还没开放", text: "准备好了会出现在这里。", tone: "quiet", retry: false };
  if (err.unknownRoute) return { kind: "noRoad", title: "这里还没有路", text: "这里还在建设，先去别处看看。", tone: "quiet", retry: false };
  // 手机号、邮箱注册那六个码（I108）：回 401、403、409、410 都一样，说没能完成这一步、给那一句人话——不说「需要先登录」「这里看不到了」
  // 「情况刚有变化」；原地再点也没用，不给「重试」（后端说 retryable 也不给）
  if (playerTextForCode(err.code)) return { kind: "failed", title: "没能完成这一步", text, tone: "error", retry: false };
  if (err.isAuth || err.status === 401) return { kind: "login", title: err.code === "SESSION_EXPIRED" ? "登录状态已过期" : "需要先登录", text, tone: "error", retry: false };
  if (known) return { kind: "choose", title: known.title, text, tone: "error", retry: false };
  if (err.code === "CSRF_FAILED") return { kind: "failed", title: "没能完成这一步", text: "登录状态有变化，刷新一下再试。", tone: "error", retry: false };
  if (err.status === 403 || err.code === "FORBIDDEN") return { kind: "forbidden", title: "这里看不到了", text, tone: "error", retry: false };
  // 后端明说“重试也没用”（UPSTREAM_UNAVAILABLE 且 retryable=false，例如识别没配好、现在放不了照片）：不说“信号暂时中断”，
  // 用中性的“现在还不行”，正文照旧是后端那句（I 10-03 01:1x，P 的 PHOTO-NC）。
  if (err.code === "UPSTREAM_UNAVAILABLE" && err.retryable === false) return { kind: "notNow", title: "现在还不行", text, tone: "error", retry: false };
  const signal =
    (err.kind === "network" && !err.fromException) || err.kind === "timeout" || err.code === "UPSTREAM_UNAVAILABLE" || err.status === 502 || err.status === 503 || err.status === 504;
  if (signal) return { kind: "signal", title: "信号暂时中断", text, tone: "error", retry: true };
  if (err.status === 429 || err.code === "RATE_LIMITED") return { kind: "slowDown", title: "歇一会儿", text, tone: "error", retry: false };
  if (err.status === 422 || err.code === "VALIDATION_FAILED") return { kind: "fixInput", title: "还有一项没填好", text, tone: "error", retry: false };
  if (err.isNotFound) return { kind: "notFound", title: "没有找到", text, tone: "error", retry: false };
  if (err.status === 409 || CONFLICT_CODES.has(err.code)) return { kind: "conflict", title: "情况刚有变化", text, tone: "error", retry: false };
  return { kind: "failed", title: "没能完成这一步", text, tone: "error", retry: err.retryable };
}
